<script setup>
import { useField, useForm } from "vee-validate";
import * as Yup from "yup";
import { EMAIL_REGEX, PHONE_REGEX } from "@/utils/constants";

const props = defineProps({
  isForSection: {
    type: Boolean,
    default: false,
  },
  orderPayload: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["submitForm", "closeForm"]);

const triggers = useYmTriggers();
const toast = useToast();
const route = useRoute();

const appVersion = computed(() =>
  route?.query?.withoutacalc === "true" ? "2" : "1",
);

const isWithCalculation = computed(() => {
  return (
    typeof props.orderPayload === "object" &&
    !!Object.keys(props.orderPayload).length
  );
});

const CONSULTATION_FORM_SCHEMA = Yup.object({
  name: Yup.string().required("Обязательное поле"),
  company: Yup.string().optional(),
  email: isWithCalculation.value
    ? Yup.string()
        .matches(EMAIL_REGEX, "Некорректная почта")
        .required("Обязательное поле")
    : Yup.string().matches(EMAIL_REGEX, "Некорректная почта").optional(),
  phone: Yup.string()
    .matches(PHONE_REGEX, "Некорректный номер")
    .required("Обязательное поле"),
  description: Yup.string().optional(),
  agree: Yup.boolean()
    .required("Подтвердите согласие")
    .oneOf([true], "Подтвердите согласие"),
});

const { handleSubmit, resetForm, isSubmitting } = useForm({
  validationSchema: CONSULTATION_FORM_SCHEMA,
});

const formFields = ref({
  name: {
    label: "Имя",
    name: "name",
    placeholder: "Имя",
    "required-field": true,
    field: useField("name"),
  },
  company: {
    label: "Наименование компании",
    name: "company",
    placeholder: "Товары из Китая",
    "required-field": false,
    field: useField("company"),
  },
  phone: {
    label: "Номер телефона",
    name: "phone",
    placeholder: "+7 (999) 999-99-99",
    "required-field": true,
    field: useField("phone"),
  },
  email: {
    label: "E-mail",
    name: "email",
    placeholder: "pochta@gmail.com",
    "required-field": isWithCalculation.value,
    field: useField("email"),
  },
  description: {
    label: "Вопрос / комментарий",
    name: "description",
    placeholder: "Текст сообщения",
    "required-field": false,
    field: useField("description"),
  },
  agree: {
    label:
      "Я даю своё согласие на обработку моих персональных данных в соответствии с политикой конфиденциальности",
    name: "agree",
    "required-field": true,
    field: useField("agree"),
  },
});

const runtimeConfig = useRuntimeConfig();

const onSubmit = handleSubmit(
  async (values) => {
    try {
      const utm = useNuxtApp().$getUtmData();
      const siteName =
        runtimeConfig.public.NUXT_PUBLIC_SITE_URL?.trim() || "avia73.pro";

      const body = {
        ...utm,
        ...values,
        siteName,
        number: values.phone,
        siteVersion: appVersion.value,
      };

      let orderId;

      if (isWithCalculation.value) {
        body.payload = props.orderPayload;
        const res = await $fetch("/api/local/order/web-delivery", {
          baseURL: runtimeConfig.public.API_URL,
          method: "POST",
          body,
        });
        orderId = res?.id;
      } else {
        await $fetch("/api/local/siteCalls", {
          baseURL: runtimeConfig.public.API_URL,
          method: "POST",
          body,
        });
      }

      triggers.reachGoal("write_to_us");

      toast.showSuccess("Заявка успешно отправлена");
      resetForm();
      emit("submitForm", orderId);
    } catch (error) {
      console.log(error);
      toast.showError("Произошла ошибка, попробуйте позже");
    }
    emit("closeForm");
  },
  () => {
    toast.showError("Заполните обязательные поля");
  },
);
</script>

<template>
  <form
    class="consultation-section__form"
    :class="{ 'consultation-section__form--section': isForSection }"
    novalidate
    @submit="onSubmit"
  >
    <UiInputText
      v-bind="formFields.name"
      v-model="formFields.name.field.value"
      :error="formFields.name.field.errorMessage"
    />
    <UiInputText
      v-bind="formFields.company"
      v-model="formFields.company.field.value"
      :error="formFields.company.field.errorMessage"
    />
    <UiInputPhone
      v-bind="formFields.phone"
      v-model="formFields.phone.field.value"
      :error="formFields.phone.field.errorMessage"
    />
    <UiInputText
      v-bind="formFields.email"
      v-model="formFields.email.field.value"
      :error="formFields.email.field.errorMessage"
    />
    <UiInputTextarea
      v-bind="formFields.description"
      v-model="formFields.description.field.value"
      :error="formFields.description.field.errorMessage"
      rows="5"
      class="form__comments"
    />
    <UiInputCheckbox
      v-bind="formFields.agree"
      v-model="formFields.agree.field.value"
      :error="formFields.agree.field.errorMessage"
      class="form__agreement-checkbox"
    />
    <button
      type="submit"
      class="button consultation-section__button"
      :disabled="isSubmitting"
    >
      Отправить
    </button>
  </form>
</template>

<style lang="scss" scoped>
.consultation-section__form {
  max-width: 528px;
  width: 100%;
  height: min-content;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: min-content;
  gap: 20px;
}
.consultation-section__form.consultation-section__form--section {
  max-width: none;
  width: 100%;
}
.consultation-section__button {
  width: 100%;
}

@media (min-width: 1240px) {
  .consultation-section__form.consultation-section__form--section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: min-content;
    gap: 20px 24px;
    height: min-content;
    .form__comments {
      grid-column: span 2;
    }
    .form__agreement-checkbox {
      grid-column: span 2;
    }
  }
}
</style>
